import { useState } from "react";
import { useParams } from "react-router";
import {
  useCostTypes,
  usePortfolioCosts,
  useProductCostClasses,
} from "@/lib/hooks/use-portfolio-collections";
import { useT } from "@/lib/i18n/use-t";
import { cn } from "@/lib/utils";
import { TableCardSkeleton } from "../skeleton-loaders";
import { CostTypesTable } from "./cost-types-table";
import { CurrencyFeesCard } from "./currency-fees-card";
import { ManualFeesTable } from "./manual-fees-table";
import { T } from "./page.i18n";
import { PortfolioCostsTable } from "./portfolio-costs-table";
import { ProductCostClassesTable } from "./product-cost-classes-table";

type View = "product" | "portfolio" | "manual" | "currency";

export default function CostsPage() {
  const { id } = useParams<{ id: string }>();
  const t = useT(T);
  const [view, setView] = useState<View>("product");

  const { costTypes, isLoading: costTypesLoading } = useCostTypes(id!);
  const { portfolioCosts, isLoading: portfolioCostsLoading } = usePortfolioCosts(id!);
  const { productCostClasses, isLoading: costClassesLoading } = useProductCostClasses(id!);

  if (costTypesLoading || portfolioCostsLoading || costClassesLoading) {
    return (
      <div className="space-y-6">
        <TableCardSkeleton columns={4} rows={3} />
        <TableCardSkeleton columns={5} rows={3} />
      </div>
    );
  }

  // Manual flat fees and classic bracket-based portfolio costs share one table.
  const manualFees = (portfolioCosts ?? []).filter((pc: any) => pc.isManual);
  const bracketCosts = (portfolioCosts ?? []).filter((pc: any) => !pc.isManual);

  const tabs: { key: View; label: string }[] = [
    { key: "product", label: t.productCosts },
    { key: "manual", label: t.manual },
    { key: "portfolio", label: t.portfolioCosts },
    { key: "currency", label: t.currency },
  ];

  return (
    <div>
      <nav className="flex gap-4 border-b mb-6">
        {tabs.map((tab) => {
          const isActive = view === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => setView(tab.key)}
              className={cn(
                "pb-2 text-sm font-medium transition-colors relative",
                isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground",
              )}
            >
              {tab.label}
              {isActive && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />}
            </button>
          );
        })}
      </nav>

      {view === "product" && <CostTypesTable portfolioCollectionId={id!} costTypes={costTypes} />}

      {view === "portfolio" && (
        <PortfolioCostsTable portfolioCollectionId={id!} portfolioCosts={bracketCosts} />
      )}

      {view === "manual" && (
        <div className="space-y-8">
          <ProductCostClassesTable
            portfolioCollectionId={id!}
            productCostClasses={productCostClasses}
          />
          <ManualFeesTable portfolioCollectionId={id!} manualFees={manualFees} />
        </div>
      )}

      {view === "currency" && <CurrencyFeesCard portfolioCollectionId={id!} />}
    </div>
  );
}
